// 明信片抽取（P-POSTCARD-RECONCEIVE v2 / B2）。
// 离线最多 1 次旅行 = 最多寄 1 封。55% 概率寄，45% 缺席（让"偶发缺席"成为叙事钩子而非故障；2026-09-18 调参：原 90%/10%）。
//
// 纯函数，rng 必须注入（GDD 01 §2.5 验收前置；domain 层不得依赖 Math.random，CI 守门 check-no-tick-accumulator）。
//
// 设计选择：rollPostcard 与 roll(gifts) 完全独立。明信片不再是礼物，
//   mailbox 与 inventory 互不污染 —— 一封明信片既不增加 inventory 也不减少。
//
// 输出：PostcardRoll 描述是否寄出、若寄出则 noteId + 寄出时间戳（now 注入）。
// 用途：settleTrip 在 awaitingReveal 写入之前调用本函数；落盘在 mailbox: PostcardEntry[]。
import type { DestinationId } from '../../save/types';
import { POSTCARD_NOTES_BY_DEST, type PostcardNote } from '../../runtime/postcardNotes';

export const POSTCARD_SEND_PROBABILITY = 0.55; // 55% 寄出，45% 缺席

// ⚠️ P6-POLISH-ENG-002 §E.4：该函数与 `lastPostcardNoteIdByDestination` 字段均已**停止用于去重**
//   —— 去重改由 mailbox 派生集合完成。两者保留不删是为了防止 migrate churn 与 import 断裂，
//   不要在新代码里重新依赖它们。
export function postcardDedupeKey(dest: DestinationId): string {
  return `postcard:${dest}`;
}

export interface PostcardRoll {
  sent: boolean; // 是否寄出（55% 寄 / 45% 不寄）
  note: PostcardNote | null; // sent=true 时取自目的地子池
  rolledAt: number; // 寄出时间戳（now 注入；不寄时也填，便于观测）
}

export interface RollPostcardCtx {
  destinationId: DestinationId;
  // 🆕 P6-POLISH-ENG-002 §E.2：去重集合**由 mailbox 派生**（`new Set(mailbox.map(e => e.noteId))`），
  //   取代旧的 `lastPostcardNoteIdByDestination`（只记"上一条"，A→B→A→B 会反复出现 A 和 B）。
  //   这次改动的性质：**连续不重复 ⟹ 全.GroupBy 不重复** —— 已获得的被整体剔除，剩下的不可能重复。
  receivedNoteIds: ReadonlySet<string>;
  // 用于在**剔除后的剩余池**里确定性轮换：`idx = tripCount % remaining.length`。
  //   刻意**不额外消耗 rng**（否则 rng 从 1 次变 2 次，既有脚本化 rng 测试序列全断）。
  //   离线回归最多 1 次旅行，每次结算调用一次 → tripCount 单调递增、不可重入。
  tripCount: number;
}

// 在目的地子池（POSTCARD_NOTES_BY_DEST[dest]，3 条）里挑一条**还没收到过**的。
// 🆕 抽完即止（07 §E.3 方案 E-1）：该目的地 3 张集齐 → remaining 为空 → 本次不寄，
//   与"缺席"同语义（不额外提示玩家，避免暴露机制）。
function pickNoteId(dest: DestinationId, received: ReadonlySet<string>, tripCount: number): PostcardNote | null {
  const remaining = POSTCARD_NOTES_BY_DEST[dest].filter((n) => !received.has(n.id));
  if (remaining.length === 0) return null;
  const idx = tripCount % remaining.length;
  return (remaining[idx] ?? remaining[0]) as PostcardNote;
}

export function rollPostcard(rng: () => number, ctx: RollPostcardCtx, now: number): PostcardRoll {
  // ① 是否寄出 —— rng 消耗次数与改造前保持一致（**1 次**，既有序列不受影响）
  const sent = rng() < POSTCARD_SEND_PROBABILITY;
  if (!sent) {
    return { sent: false, note: null, rolledAt: now };
  }
  const note = pickNoteId(ctx.destinationId, ctx.receivedNoteIds, ctx.tripCount);
  // 剩余池为空（该目的地 3 张已集齐）→ 本次不寄
  if (!note) {
    return { sent: false, note: null, rolledAt: now };
  }
  return { sent: true, note, rolledAt: now };
}
